import { useState } from "react";
import SectionTitle from "./SectionTitle";

const QUOTES = [
  {
    id: "q1",
    name: "早高峰的骑车人",
    place: "事发街口 · 第一位到场",
    base: 182,
    text: "我当时在对面等灯，先听见动静再看过去，画面里那人的先后顺序和后面放的实拍是能对上的，就是角度反了。",
    tag: "目击者说",
  },
  {
    id: "q2",
    name: "临街铺面店员",
    place: "视野正面 · 拍到全程",
    base: 96,
    text: "我从店里出来时已经晚了几秒，最先几步没看到。后来截图里那个时间戳，正好是我回店里那段，说明转述有遗漏。",
    tag: "补充证言",
  },
];

const TABLE = [
  { who: "目击 A", claim: "先看到侧向来车", clue: "流出图有对应方位", status: "相符" },
  { who: "目击 B", claim: "人数说法对不上", clue: "正机位画面存疑", status: "存疑" },
  { who: "截图", claim: "时间顺序被调换", clue: "转发链路缺一环", status: "待证" },
  { who: "实拍", claim: "侧向机位已补上", clue: "时间戳核对通过", status: "相符" },
];

const STATUS_STYLE: Record<string, string> = {
  相符: "bg-secondary-100 text-secondary-700",
  存疑: "bg-accent-100 text-accent-700",
  待证: "bg-primary-100 text-primary-700",
};

export default function Witness() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [count, setCount] = useState<Record<string, number>>({ q1: 182, q2: 96 });

  const toggle = (id: string, base: number) => {
    const isOn = !liked[id];
    setLiked((prev) => ({ ...prev, [id]: isOn }));
    setCount((prev) => ({ ...prev, [id]: isOn ? base + 1 : base }));
  };

  return (
    <section id="witness" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <SectionTitle
          index="04"
          kicker="目击者说 · 线索对表"
          title={<>听人怎么说之前，先把口径摆在一起</>}
          lead="目击转述提供方向，截图线索负责校时。这里把各人说法和对应证据并排列出，相符、存疑还是待证，一眼看得明白。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8">
          {/* witness quotes */}
          <div className="space-y-5 lg:col-span-7">
            {QUOTES.map((q) => (
              <article
                key={q.id}
                className="rounded-lg border border-foreground-200/70 bg-background-100 p-6 md:p-7"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 font-serif text-sm font-bold text-secondary-700">
                      {q.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{q.name}</p>
                      <p className="text-[12px] text-foreground-400">{q.place}</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] text-foreground-600">{q.tag}</span>
                </div>
                <p className="mt-4 border-l-2 border-primary-500/70 pl-4 text-[15px] leading-7 text-foreground-700">
                  “{q.text}”
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={() => toggle(q.id, q.base)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] transition-colors whitespace-nowrap ${
                      liked[q.id]
                        ? "border-primary-500 bg-primary-100 text-primary-700"
                        : "border-foreground-200 text-foreground-500 hover:border-primary-400 hover:text-primary-600"
                    }`}
                    aria-pressed={!!liked[q.id]}
                  >
                    <i className={`${liked[q.id] ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-[13px]`} aria-hidden="true" />
                    赞同 {count[q.id]}
                  </button>
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground-400">
                    <i className="ri-chat-3-line text-[13px]" aria-hidden="true" />讨论 24
                  </span>
                </div>
              </article>
            ))}
          </div>

          {/* clue compare table */}
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-foreground-200 bg-background-100">
              <div className="flex items-center gap-2 border-b border-foreground-200/70 px-5 py-4">
                <i className="ri-shield-check-line text-accent-600" aria-hidden="true" />
                <span className="text-sm font-medium text-foreground-950">本事件 · 截图线索对表</span>
              </div>
              <ul className="divide-y divide-foreground-200/70">
                {TABLE.map((r) => (
                  <li key={r.who + r.claim} className="px-5 py-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] text-foreground-400">{r.who}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_STYLE[r.status]}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[14px] leading-6 text-foreground-800">{r.claim}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-[12px] text-foreground-400">
                      <i className="ri-link-m text-[13px]" aria-hidden="true" />{r.clue}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="border-t border-foreground-200/70 px-5 py-3">
                <p className="text-[12px] text-foreground-400">标注为“存疑 / 待证”的口径，会在补到画面后更正。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}